import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import OpenIcon from '@mui/icons-material/OpenInNew'
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined'
import type { EtatSite } from '@iobiz/shared'
import { ApiError, api } from '../../api/client.js'
import { creerSiteAdmin, lireSiteAdmin, publierSiteAdmin } from './api.js'

/**
 * Les sites vitrines, vus de la PLATEFORME.
 *
 * ⭐ La création et la mise en ligne sont des gestes d'exploitant, pas de
 * commerçant : c'est la plateforme qui ouvre un site au public, et elle seule.
 * Le gérant, lui, en écrit le contenu quand il veut — les deux volontés ne se
 * confondent nulle part, ni dans les routes, ni sur cet écran.
 *
 * ⚠️ Publier n'engage pas que la boutique : un site en ligne porte le nom de la
 * plateforme autant que celui de l'enseigne. D'où le contrôle ici.
 */
interface Boutique {
  id: string
  slug: string
  name: string
  status: string
}

export function SitesVitrines(): JSX.Element {
  const [boutiques, setBoutiques] = useState<Boutique[] | null>(null)
  const [sites, setSites] = useState<Record<string, EtatSite | null>>({})
  const [occupe, setOccupe] = useState<string | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)

  async function charger(): Promise<void> {
    setErreur(null)
    try {
      const r = await api.get<{ data: Boutique[] }>('/admin/stores')
      const actives = r.data.filter((b) => b.status !== 'archived')
      setBoutiques(actives)
      // ⚠️ En parallèle, mais tolérant : une boutique dont le site est
      // illisible ne doit pas laisser tout le tableau vide.
      const etats = await Promise.all(
        actives.map((b) =>
          lireSiteAdmin(b.id)
            .then((s) => [b.id, s] as const)
            .catch(() => [b.id, null] as const),
        ),
      )
      setSites(Object.fromEntries(etats))
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Chargement impossible.')
      setBoutiques([])
    }
  }

  useEffect(() => {
    void charger()
  }, [])

  async function agir(id: string, action: () => Promise<EtatSite>): Promise<void> {
    setOccupe(id)
    setErreur(null)
    try {
      const s = await action()
      setSites((prev) => ({ ...prev, [id]: s }))
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Action impossible.')
    } finally {
      setOccupe(null)
    }
  }

  if (boutiques === null) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 200 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  return (
    <>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
        Créez le site d’une boutique, puis mettez-le en ligne. Le contenu, lui, s’écrit dans la
        boutique — Paramètres › Site vitrine.
      </Typography>

      {erreur && (
        <Alert severity="error" sx={{ mb: 4 }} onClose={() => setErreur(null)}>
          {erreur}
        </Alert>
      )}

      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Boutique</TableCell>
              <TableCell>Adresse publique</TableCell>
              <TableCell>État</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {boutiques.map((b) => {
              const site = sites[b.id]
              const enLigne = site?.statut === 'published'
              return (
                <TableRow key={b.id} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{b.name}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>/boutique/{b.slug}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={!site ? 'Aucun site' : enLigne ? 'En ligne' : 'Brouillon'}
                      color={!site ? 'default' : enLigne ? 'success' : 'warning'}
                      variant={enLigne ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box
                      sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}
                    >
                      {!site && (
                        <Button
                          size="small"
                          variant="contained"
                          disabled={occupe === b.id}
                          onClick={() => void agir(b.id, () => creerSiteAdmin(b.id))}
                        >
                          Créer le site
                        </Button>
                      )}
                      {site && (
                        <>
                          {/* ⭐ Voir AVANT de publier : c'est ce qui permet de
                              refuser une mise en ligne sur pièces plutôt que
                              sur parole. */}
                          <Button
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() =>
                              window.open(`/admin/vitrine/${b.id}`, '_blank', 'noopener')
                            }
                          >
                            Prévisualiser
                          </Button>
                          {enLigne && (
                            <Button
                              size="small"
                              startIcon={<OpenIcon />}
                              onClick={() =>
                                window.open(`/boutique/${b.slug}`, '_blank', 'noopener')
                              }
                            >
                              Voir
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant={enLigne ? 'outlined' : 'contained'}
                            color={enLigne ? 'inherit' : 'primary'}
                            disabled={occupe === b.id}
                            onClick={() => void agir(b.id, () => publierSiteAdmin(b.id, !enLigne))}
                          >
                            {enLigne ? 'Retirer du web' : 'Publier'}
                          </Button>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </Paper>
    </>
  )
}
